import { FC, Fragment, createPortal } from "preact/compat";
import { useEffect } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useSignalEffect } from "@preact/signals";
import { IconButton } from "~/components";
import { usePortalTarget } from "~/hooks";
import { ENV } from "../env";
import { useApp } from "../store";
import RefreshIcon from "@material-design-icons/svg/outlined/refresh.svg";
import WifiOffIcon from "@material-design-icons/svg/outlined/wifi_off.svg";
import cn from "classnames";

const KEYBOARD_DEBOUNCE_MS = 300;

const AppStatus: FC = () => {
    const {
        status: { lost, online, apiLost },
        mobile,
    } = useApp();
    const target = usePortalTarget();

    useEffect(() => {
        document.documentElement.classList.add(ENV);
        document.body.classList.toggle("mobile", mobile.isMobile);
        document.body.classList.toggle("android", mobile.isAndroid);
        document.body.classList.toggle("ios", mobile.isIOS);

        if (!mobile.isMobile) {
            return;
        }

        const style = document.createElement("style");

        style.innerHTML = `
        a {
            -webkit-user-drag: none;
            user-drag: none;
        }
        * {
            -webkit-tap-highlight-color: transparent;
            -webkit-user-select: none;
            user-select: none;
        }
        `;

        document.head.appendChild(style);

        window.document.addEventListener("focusin", (event) => {
            if (
                event.target instanceof Element &&
                (event.target.tagName === "INPUT" ||
                    event.target.tagName === "TEXTAREA") &&
                !(event.target as HTMLInputElement).readOnly
            ) {
                mobile.focus.value = true;
                document.body.classList.add("input-focus");
            }
        });

        window.document.addEventListener("focusout", (event) => {
            if (mobile.focus.peek()) {
                mobile.focus.value = false;
                document.body.classList.remove("input-focus");
            }

            if (mobile.isIOS && !event.relatedTarget) {
                window.scrollTo(0, 0);
            }
        });

        const set1Vh = (vh: number) => {
            document.documentElement.style.setProperty("--dvh", `${vh}px`);
        };

        const setKeyboardHeight = (height: number) => {
            document.documentElement.style.setProperty(
                "--h-keyboard",
                `${height}px`,
            );
        };

        if (mobile.isIOS) {
            const initialHeight = window.innerHeight;
            let requestId = 0;
            let requestTs = 0;

            const tick = () => {
                const h = window.visualViewport?.height ?? window.innerHeight;
                const vh = h * 0.01;

                set1Vh(vh);

                // in mobile apps there's a height problem when keyboard is shown
                // whenever resize happens, we read the new height and set --dvh css variable
                //
                if (
                    Date.now() - requestTs < KEYBOARD_DEBOUNCE_MS &&
                    h < initialHeight
                ) {
                    requestId = window.requestAnimationFrame(tick);
                }
            };

            const handleResize = () => {
                requestTs = Date.now();
                window.cancelAnimationFrame(requestId);
                requestId = window.requestAnimationFrame(tick);
            };

            if (window.visualViewport) {
                window.visualViewport.addEventListener("resize", handleResize);
            } else {
                window.addEventListener("resize", handleResize);
            }
        } else if (mobile.isAndroid) {
            window.Android ??= {};
            window.Android.onKeyboardShow = (height) => {
                set1Vh((window.innerHeight - height) * 0.01);
                setKeyboardHeight(height);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useSignalEffect(() => {
        document.body.classList.toggle("lost", lost.value);
    });

    if (!target.value) {
        return null;
    }

    return createPortal(
        <Transition
            show={lost.value}
            enter="transition duration-75"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
            as={Fragment}
        >
            <div
                className={cn(
                    "fixed bottom-6 left-1/2 z-[300] -translate-x-1/2",
                    "flex items-center justify-center gap-x-4 bg-gradient-decor-to-r",
                    "rounded-full font-bold text-white shadow-lg md:text-lg",
                    "w-[calc(100vw-2rem)] px-4 py-2 md:w-auto md:py-4",
                )}
            >
                {!online.value && (
                    <WifiOffIcon className="size-6 flex-shrink-0 fill-current md:size-8" />
                )}
                <span>
                    {online.value
                        ? "Connection to the server has been lost."
                        : "You are offline. Please check your internet connection."}
                </span>
                {apiLost.value && (
                    <IconButton
                        flat
                        title="Refresh the page"
                        onClick={() => window.location.reload()}
                    >
                        <RefreshIcon className="size-6 fill-current md:size-8" />
                    </IconButton>
                )}
            </div>
        </Transition>,
        target.value,
    );
};

export default AppStatus;
